import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Chrome, Zap, Eye, PhoneCall, LogOut, ArrowRight } from 'lucide-react';
import supabase from '../lib/supabaseClient';

const EXTENSION_URL = 'https://chromewebstore.google.com/detail/mfpjnpbdnlgbockeagdlooeohldgabnj';

export default function WelcomePage() {
  const { user } = useAuth();
  const navigate = useNavigate();

  const handleLogout = async () => {
    await supabase.auth.signOut();
    navigate('/login', { replace: true });
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-cyan-50/30">
      {/* Header */}
      <header className="w-full px-6 py-4 flex items-center justify-between max-w-4xl mx-auto">
        <img src="/newSurFoxLogo1.png" alt="SurFox AI" className="h-10 object-contain" />
        <button
          onClick={handleLogout}
          className="flex items-center gap-2 text-sm text-slate-500 hover:text-slate-700 transition-colors px-3 py-2 rounded-lg hover:bg-slate-100"
        >
          <LogOut className="w-4 h-4" />
          Log out
        </button>
      </header>

      {/* Main content */}
      <main className="max-w-3xl mx-auto px-6 py-12">
        {/* Welcome section */}
        <div className="text-center mb-16">
          <h1 className="text-4xl font-bold text-slate-900 mb-4">
            Welcome to SurFox AI
          </h1>
          <p className="text-lg text-slate-600 max-w-xl mx-auto leading-relaxed">
            Your manager has set everything up. When hot leads are ready for you, they'll appear right in your browser.
          </p>
        </div>

        {/* How it works */}
        <div className="mb-16">
          <h2 className="text-center text-sm font-semibold uppercase tracking-wider text-slate-400 mb-10">
            How it works
          </h2>

          <div className="grid md:grid-cols-3 gap-6">
            {/* Step 1 */}
            <div className="relative bg-white rounded-2xl border border-slate-200 p-6 text-center shadow-sm">
              <div className="absolute -top-3 left-1/2 -translate-x-1/2 w-7 h-7 bg-slate-900 text-white text-sm font-bold rounded-full flex items-center justify-center">
                1
              </div>
              <div className="w-12 h-12 bg-cyan-50 rounded-xl flex items-center justify-center mx-auto mb-4 mt-2">
                <Zap className="w-6 h-6 text-cyan-600" />
              </div>
              <h3 className="font-semibold text-slate-900 mb-2">SurFox AI qualifies your leads</h3>
              <p className="text-sm text-slate-500 leading-relaxed">
                Our AI engages and scores leads 24/7. When someone is ready to buy, you'll know.
              </p>
            </div>

            {/* Step 2 */}
            <div className="relative bg-white rounded-2xl border border-slate-200 p-6 text-center shadow-sm">
              <div className="absolute -top-3 left-1/2 -translate-x-1/2 w-7 h-7 bg-slate-900 text-white text-sm font-bold rounded-full flex items-center justify-center">
                2
              </div>
              <div className="w-12 h-12 bg-cyan-50 rounded-xl flex items-center justify-center mx-auto mb-4 mt-2">
                <Eye className="w-6 h-6 text-cyan-600" />
              </div>
              <h3 className="font-semibold text-slate-900 mb-2">Hot leads appear in your browser</h3>
              <p className="text-sm text-slate-500 leading-relaxed">
                The Chrome extension shows you exactly who to call, what they care about, and what to say.
              </p>
            </div>

            {/* Step 3 */}
            <div className="relative bg-white rounded-2xl border border-slate-200 p-6 text-center shadow-sm">
              <div className="absolute -top-3 left-1/2 -translate-x-1/2 w-7 h-7 bg-slate-900 text-white text-sm font-bold rounded-full flex items-center justify-center">
                3
              </div>
              <div className="w-12 h-12 bg-cyan-50 rounded-xl flex items-center justify-center mx-auto mb-4 mt-2">
                <PhoneCall className="w-6 h-6 text-cyan-600" />
              </div>
              <h3 className="font-semibold text-slate-900 mb-2">You dial and close</h3>
              <p className="text-sm text-slate-500 leading-relaxed">
                One click to call. AI-generated scripts. Log outcomes. That's it.
              </p>
            </div>
          </div>
        </div>

        {/* Get started steps */}
        <div className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden mb-12">
          <div className="px-6 py-4 bg-slate-50 border-b border-slate-200">
            <h2 className="font-semibold text-slate-900">Get started</h2>
          </div>

          <div className="divide-y divide-slate-100">
            {/* Step 1: Install extension */}
            <div className="px-6 py-5 flex items-start gap-4">
              <div className="w-8 h-8 bg-cyan-500 text-white text-sm font-bold rounded-full flex items-center justify-center shrink-0 mt-0.5">
                1
              </div>
              <div className="flex-1">
                <h3 className="font-medium text-slate-900 mb-1">Install the Chrome Extension</h3>
                <p className="text-sm text-slate-500 mb-4">
                  This is where your hot leads will appear. Takes less than a minute.
                </p>
                <a
                  href={EXTENSION_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 px-6 py-3 bg-slate-900 hover:bg-slate-800 text-white
                             font-semibold rounded-xl transition-colors shadow-sm text-sm"
                >
                  <Chrome className="w-5 h-5" />
                  Install SurFox AI Extension
                  <ArrowRight className="w-4 h-4" />
                </a>
              </div>
            </div>

            {/* Step 2: Log in */}
            <div className="px-6 py-5 flex items-start gap-4">
              <div className="w-8 h-8 bg-slate-200 text-slate-600 text-sm font-bold rounded-full flex items-center justify-center shrink-0 mt-0.5">
                2
              </div>
              <div>
                <h3 className="font-medium text-slate-900 mb-1">Log in with your credentials</h3>
                <p className="text-sm text-slate-500">
                  Use the same email and password you just created to sign into the extension.
                </p>
              </div>
            </div>

            {/* Step 3: Wait */}
            <div className="px-6 py-5 flex items-start gap-4">
              <div className="w-8 h-8 bg-slate-200 text-slate-600 text-sm font-bold rounded-full flex items-center justify-center shrink-0 mt-0.5">
                3
              </div>
              <div>
                <h3 className="font-medium text-slate-900 mb-1">Wait for hot leads</h3>
                <p className="text-sm text-slate-500">
                  Your manager will assign campaigns to you. When leads go hot, they'll appear in your extension automatically.
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="text-center text-sm text-slate-400 pb-8">
          <p>Questions? Ask your manager or contact <a href="mailto:support@getsurfox.com" className="text-cyan-600 hover:text-cyan-700 transition-colors">support@getsurfox.com</a></p>
        </div>
      </main>
    </div>
  );
}
